import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MOODS, type MoodKey } from '@qq/shared';
import { api } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { MoodFace } from '../art/MoodFace.js';

interface TodayCheckIn {
  learningDay: string;
  done: boolean;
  mood: MoodKey | null;
  note: string | null;
}

/**
 * "How are you feeling today?"
 *
 * Asked once a day, before the adventure, and answered - the parent's decision,
 * so that a day never goes unrecorded. Two consequences are designed around:
 *
 *  1. **No option is preselected and nothing is defaulted.** A child standing
 *     between themselves and their game will take the fastest route through it.
 *     Making them aim at a specific large face is the only real defence, so the
 *     targets are big and the button stays dead until one is chosen.
 *  2. **A failure must never trap them.** If the save cannot reach the server,
 *     the child is let through to their adventure rather than being held at a
 *     screen they cannot pass. Losing one day's check-in is a small cost; a
 *     five-year-old locked out of the app by a wifi drop is not.
 *
 * This screen does NOT tell the child that their grown-ups read it - a parent
 * decision, taken so the answers stay unguarded. See the note in
 * packages/shared/src/checkin.ts for what that trades away.
 *
 * Nothing is earned here. No collectible, no streak, no praise for picking a
 * happy face: rewarding cheerful answers teaches a child to lie to the people
 * who most need the truth. That one is not up for changing.
 */
export function CheckIn() {
  const navigate = useNavigate();
  const session = useSession();
  const queryClient = useQueryClient();
  const [mood, setMood] = useState<MoodKey | null>(null);
  const [note, setNote] = useState('');

  const today = useQuery({
    queryKey: ['checkin', 'today'],
    queryFn: () => api<TodayCheckIn>('/child/checkin/today'),
  });

  const save = useMutation({
    mutationFn: (chosen: MoodKey) =>
      api('/child/checkin', {
        method: 'POST',
        body: { mood: chosen, note: note.trim() || undefined },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['checkin', 'today'] });
      navigate('/play', { replace: true });
    },
    // The check-in cannot be skipped, which means a failure here would trap a
    // child at a screen they have no way past. It does not: after a failed
    // attempt they are let through to their adventure. One lost check-in beats
    // a five-year-old locked out of the app by a dropped connection.
    onError: () => navigate('/play', { replace: true }),
  });

  const child = session.data?.child;
  if (session.isPending) return <main className="screen">Just a moment&hellip;</main>;
  if (!child) return <Navigate to="/" replace />;

  const alreadyDone = today.data?.done ?? false;
  const chosen = mood ?? today.data?.mood ?? null;

  return (
    <main className="screen checkin">
      <h1>How are you feeling today, {child.nickname}?</h1>

      <p className="checkin-plain">
        Whatever you pick is okay. There is no right answer, and nothing happens differently either
        way.
      </p>

      <div className="mood-row">
        {[...MOODS].reverse().map((m) => (
          <button
            key={m.key}
            type="button"
            className={`mood-choice${chosen === m.key ? ' mood-choice--picked' : ''}`}
            aria-pressed={chosen === m.key}
            onClick={() => setMood(m.key)}
          >
            <MoodFace mood={m.face} size={96} title={m.childLabel} />
            <span>{m.childLabel}</span>
          </button>
        ))}
      </div>

      <label className="checkin-note">
        Want to say anything? You don&rsquo;t have to.
        <textarea
          rows={5}
          value={note || today.data?.note || ''}
          placeholder="Anything at all. Or leave it empty."
          onChange={(e) => setNote(e.target.value)}
        />
      </label>

      <div className="checkin-actions">
        {/* Dead until a face is chosen. Nothing is preselected, so there is no
            default a child can blow straight past on the way to their game. */}
        <button
          type="button"
          className="btn btn--big"
          disabled={!chosen || save.isPending}
          onClick={() => chosen && save.mutate(chosen)}
        >
          {save.isPending ? 'Sending…' : alreadyDone ? 'Change my answer' : 'Send it'}
        </button>
        {!chosen && <p className="checkin-plain">Pick the face that fits today.</p>}
      </div>
    </main>
  );
}
